<div class="project-tasks-page">
  <section class="hero">
    <div class="hero-copy">
      <p class="eyebrow">Task Studio</p>
      <h2>{{ workspaceLabel }}</h2>
      <p class="sub">Plan standalone work, route deliverables into projects, and break execution into subtasks that move parent progress automatically.</p>
    </div>
    <div class="hero-actions">
      <button mat-stroked-button color="primary" (click)="loadTasks(selectedTask?.id || null)">
        <mat-icon>refresh</mat-icon>
        Refresh
      </button>
      <button mat-flat-button color="primary" (click)="openCreateTaskModal()">
        <mat-icon>add</mat-icon>
        New Task
      </button>
    </div>
  </section>

  <section class="stats-grid">
    <article class="stat-card">
      <span>Total Tasks</span>
      <strong>{{ totalTasks }}</strong>
      <small>{{ workspaceLabel }}</small>
    </article>
    <article class="stat-card">
      <span>Open Work</span>
      <strong>{{ openTasks }}</strong>
      <small>{{ blockedTasks }} blocked</small>
    </article>
    <article class="stat-card">
      <span>Completed</span>
      <strong>{{ completedTasks }}</strong>
      <small>{{ totalTasks ? ((completedTasks / totalTasks) * 100 | number:'1.0-0') : 0 }}% finish rate</small>
    </article>
    <article class="stat-card warning">
      <span>Overdue</span>
      <strong>{{ overdueTasks }}</strong>
      <small>Need attention</small>
    </article>
  </section>

  <section class="workspace-bar">
    <mat-form-field appearance="outline" class="project-select">
      <mat-label>Workspace</mat-label>
      <mat-select [(value)]="projectFilter" (selectionChange)="onWorkspaceFilterChange()">
        <mat-option value="all">All tasks</mat-option>
        <mat-option value="standalone">Standalone tasks</mat-option>
        <mat-option *ngFor="let p of projects" [value]="p.id">{{ p.name }}</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="search-field">
      <mat-label>Search tasks</mat-label>
      <input matInput [(ngModel)]="searchTerm" placeholder="Search title, owner, assignee, priority" />
      <mat-icon matSuffix>search</mat-icon>
    </mat-form-field>

    <mat-form-field appearance="outline" class="status-field">
      <mat-label>Status</mat-label>
      <mat-select [(value)]="statusFilter" (selectionChange)="onWorkspaceFilterChange()">
        <mat-option value="All">All</mat-option>
        <mat-option *ngFor="let s of statusOptions" [value]="s">{{ s }}</mat-option>
      </mat-select>
    </mat-form-field>
  </section>

  <mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>

  <div class="workspace-grid">
    <aside class="task-rail panel">
      <div class="rail-head">
        <div>
          <p class="eyebrow">Task Rail</p>
          <h3>{{ filteredRows.length }} Items</h3>
        </div>
        <span class="chip muted">{{ statusFilter }}</span>
      </div>

      <div class="task-card-list" *ngIf="filteredRows.length; else noTasks">
        <button
          type="button"
          class="task-card"
          *ngFor="let row of filteredRows"
          [class.selected]="selectedTask?.id === row.id"
          [class.overdue]="isOverdue(row)"
          (click)="editTask(row)"
        >
          <div class="task-card-top">
            <div class="task-title-block">
              <strong>{{ row.title }}</strong>
              <small>{{ row.assignedStaffName || 'Unassigned' }}</small>
            </div>
            <span class="priority-pill" [ngClass]="priorityClass(row.priority)">{{ row.priority }}</span>
          </div>

          <div class="task-card-meta">
            <span class="chip muted">{{ row.projectName || 'Standalone' }}</span>
            <span class="chip" [ngClass]="statusClass(row.status)">{{ row.status }}</span>
            <span class="tone" [ngClass]="completionTone(row)">{{ progressLabel(row) }}</span>
          </div>

          <div class="task-card-progress">
            <mat-progress-bar mode="determinate" [value]="row.progressPercent || 0"></mat-progress-bar>
            <div class="progress-meta">
              <span>{{ row.progressPercent | number:'1.0-0' }}%</span>
              <span>{{ dueState(row) }}</span>
            </div>
          </div>

          <div class="task-card-footer">
            <span>{{ row.subtaskCount || 0 }} subtasks</span>
            <span>{{ row.commentCount || 0 }} updates</span>
          </div>
        </button>
      </div>

      <ng-template #noTasks>
        <div class="empty-state">No tasks match the current filters.</div>
      </ng-template>
    </aside>

    <section class="detail-studio panel" *ngIf="selectedTask; else emptyDetail">
      <div class="detail-head">
        <div>
          <p class="eyebrow">Task Detail</p>
          <h3>{{ selectedTask.title }}</h3>
          <p class="detail-sub">Edit task detail, track execution, and manage child work without leaving the workspace.</p>
        </div>
        <div class="detail-badges">
          <span class="chip muted">{{ selectedTask.projectName || 'Standalone' }}</span>
          <span class="chip" [ngClass]="statusClass(selectedTask.status)">{{ selectedTask.status }}</span>
          <span class="priority-pill" [ngClass]="priorityClass(selectedTask.priority)">{{ selectedTask.priority }}</span>
        </div>
      </div>

      <div class="focus-strip">
        <div>
          <span>Owner</span>
          <strong>{{ selectedTask.createdByName || 'Unknown' }}</strong>
        </div>
        <div>
          <span>Assignee</span>
          <strong>{{ selectedTask.assignedStaffName || 'Unassigned' }}</strong>
        </div>
        <div>
          <span>Due</span>
          <strong>{{ selectedTask.dueDate ? (selectedTask.dueDate | date:'mediumDate') : 'No due date' }}</strong>
        </div>
        <div>
          <span>Subtasks</span>
          <strong>{{ selectedTask.completedSubtaskCount || 0 }}/{{ selectedTask.subtaskCount || 0 }}</strong>
        </div>
      </div>

      <div class="task-owner">
        <span>Project: {{ selectedTask.projectName || 'Standalone task' }}</span>
        <span *ngIf="taskHasSubtasks(selectedTask)">Parent progress is auto-calculated from subtask completion.</span>
      </div>

      <div class="subtasks-panel">
        <div class="section-head">
          <div>
            <p class="eyebrow">Subtasks</p>
            <h4>Execution Breakdown</h4>
          </div>
          <button mat-stroked-button color="primary" type="button" (click)="openCreateTaskModal(selectedTask)">
            <mat-icon>add</mat-icon>
            Add Subtask
          </button>
        </div>

        <div class="subtask-list" *ngIf="selectedSubtasks.length; else noSubtasks">
          <button type="button" class="subtask-item" *ngFor="let subtask of selectedSubtasks" (click)="editTask(subtask)">
            <div class="subtask-copy">
              <strong>{{ subtask.title }}</strong>
              <span>{{ subtask.assignedStaffName || 'Unassigned' }}</span>
            </div>
            <div class="subtask-meta">
              <span class="chip" [ngClass]="statusClass(subtask.status)">{{ subtask.status }}</span>
              <span>{{ subtask.progressPercent | number:'1.0-0' }}%</span>
            </div>
          </button>
        </div>

        <ng-template #noSubtasks>
          <div class="empty-state compact">No subtasks yet. Add child items to make parent progress move automatically.</div>
        </ng-template>
      </div>

      <div class="form-grid">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Task Title</mat-label>
          <input matInput [(ngModel)]="form.title" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Description</mat-label>
          <textarea #taskDescriptionInput matInput rows="5" [(ngModel)]="form.description" [disabled]="selectedTask && !canEditDescription(selectedTask)" (input)="onTaskDescriptionInput($event)" (click)="onTaskDescriptionInput($event)"></textarea>
          <mat-hint *ngIf="selectedTask && !canEditDescription(selectedTask)">Only the task owner can edit the description.</mat-hint>
        </mat-form-field>
        <div class="mention-box full-width" *ngIf="taskMentionSuggestions().length">
          <button type="button" class="mention-option" *ngFor="let staff of taskMentionSuggestions()" (click)="insertTaskMention(staff, taskDescriptionInput)">
            <strong>{{ staff.fullName }}</strong>
            <span>{{ staff.position || 'Staff' }}</span>
          </button>
        </div>

        <mat-form-field appearance="outline">
          <mat-label>Priority</mat-label>
          <mat-select [(value)]="form.priority">
            <mat-option *ngFor="let p of priorityOptions" [value]="p">{{ p }}</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Status</mat-label>
          <mat-select [(value)]="form.status">
            <mat-option *ngFor="let s of statusOptions" [value]="s">{{ s }}</mat-option>
          </mat-select>
        </mat-form-field>

        <div class="progress-field" [class.readonly]="taskHasSubtasks(selectedTask)">
          <div class="field-top">
            <label>Progress</label>
            <strong>{{ form.progressPercent || 0 }}%</strong>
          </div>
          <mat-progress-bar color="primary" mode="determinate" [value]="form.progressPercent || 0"></mat-progress-bar>
          <input type="number" min="0" max="100" [(ngModel)]="form.progressPercent" [disabled]="taskHasSubtasks(selectedTask)" />
          <small *ngIf="taskHasSubtasks(selectedTask)">This progress is derived from completed subtasks.</small>
        </div>

        <mat-form-field appearance="outline">
          <mat-label>Assigned Staff</mat-label>
          <mat-select [(value)]="form.assignedStaffId" [disabled]="!canAssignAnyStaff()">
            <mat-option [value]="null">Unassigned</mat-option>
            <mat-option *ngFor="let s of staffs" [value]="s.id">{{ s.fullName }}</mat-option>
          </mat-select>
          <mat-hint *ngIf="!canAssignAnyStaff()">You can only assign tasks to yourself.</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Due Date</mat-label>
          <input matInput type="date" [(ngModel)]="form.dueDate" />
        </mat-form-field>

        <div class="media-upload full-width">
          <div class="section-head">
            <div>
              <p class="eyebrow">Attachments</p>
              <h4>Task Media</h4>
            </div>
            <span class="chip muted">{{ form.mediaAttachments?.length || 0 }} files</span>
          </div>
          <input #taskFileInput type="file" multiple accept="image/*,video/*,audio/*,application/pdf" (change)="onTaskMediaSelected($event)" hidden />
          <button type="button" class="dropzone" (click)="taskFileInput.click()" (dragover)="$event.preventDefault()" (drop)="onTaskMediaDrop($event)">
            <strong>Drop task media here</strong>
            <span>Images, video, audio, and PDFs can travel with the task context.</span>
          </button>
          <div class="media-list" *ngIf="form.mediaAttachments?.length">
            <div class="media-item" *ngFor="let media of form.mediaAttachments; let i = index">
              <div class="media-preview" [ngSwitch]="mediaKind(media.url, media.contentType)">
                <img *ngSwitchCase="'image'" [src]="media.url" [alt]="media.fileName" />
                <video *ngSwitchCase="'video'" [src]="media.url" controls></video>
                <audio *ngSwitchCase="'audio'" [src]="media.url" controls></audio>
                <button *ngSwitchDefault type="button" class="preview-link" (click)="openPreview(media)">{{ media.fileName }}</button>
              </div>
              <div class="media-actions">
                <span>{{ media.fileName }}</span>
                <div class="action-row">
                  <button mat-stroked-button type="button" (click)="openPreview(media)">Preview</button>
                  <button mat-stroked-button color="warn" type="button" (click)="removeTaskMedia(i)">Remove</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="actions">
        <button mat-stroked-button (click)="resetForm()">Close</button>
        <button mat-flat-button color="primary" (click)="saveTask()" [disabled]="saving || !canEditTasks()">
          {{ saving ? 'Saving...' : 'Save Task' }}
        </button>
      </div>

      <div class="comments-panel">
        <div class="comments-head">
          <div>
            <p class="eyebrow">Activity</p>
            <h4>Task Updates</h4>
          </div>
          <span class="chip muted">{{ selectedTask.commentCount || 0 }} notes</span>
        </div>

        <div class="comment-composer">
          <div class="reply-target" *ngIf="commentParentId">
            Replying to comment #{{ commentParentId }}
            <button mat-button type="button" (click)="setReplyTarget(null)">Cancel reply</button>
          </div>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Add update</mat-label>
            <textarea #taskCommentInput matInput rows="3" [(ngModel)]="commentText" placeholder="Post assignee progress, blockers, or handoff notes." (input)="onCommentInput($event)" (click)="onCommentInput($event)"></textarea>
          </mat-form-field>
          <div class="mention-box full-width" *ngIf="commentMentionSuggestions().length">
            <button type="button" class="mention-option" *ngFor="let staff of commentMentionSuggestions()" (click)="insertCommentMention(staff, taskCommentInput)">
              <strong>{{ staff.fullName }}</strong>
              <span>{{ staff.position || 'Staff' }}</span>
            </button>
          </div>
          <input #commentFileInput type="file" multiple accept="image/*,video/*,audio/*,application/pdf" (change)="onCommentMediaSelected($event)" hidden />
          <button type="button" class="dropzone compact" (click)="commentFileInput.click()" (dragover)="$event.preventDefault()" (drop)="onCommentMediaDrop($event)">
            <strong>Drop comment media here</strong>
            <span>Attach proof, screenshots, or handoff assets.</span>
          </button>
          <div class="media-list compact" *ngIf="commentAttachments.length">
            <div class="media-item" *ngFor="let media of commentAttachments; let i = index">
              <button type="button" class="preview-link" (click)="openPreview(media)">{{ media.fileName }}</button>
              <button mat-stroked-button color="warn" type="button" (click)="removeCommentMedia(i)">Remove</button>
            </div>
          </div>
          <button mat-flat-button color="accent" (click)="addComment()" [disabled]="commentSaving">
            {{ commentSaving ? 'Posting...' : 'Post Update' }}
          </button>
        </div>

        <div class="comment-list" *ngIf="selectedTask.comments?.length; else noComments">
          <div class="comment-item" *ngFor="let comment of selectedTask.comments">
            <div class="comment-meta">
              <strong>{{ comment.createdByStaffName || comment.createdByName || 'Team member' }}</strong>
              <span>{{ comment.createdAt | date:'medium' }}</span>
            </div>
            <p>{{ comment.comment }}</p>
            <div class="inline-media" *ngIf="comment.mediaAttachments?.length">
              <button type="button" class="preview-link" *ngFor="let media of comment.mediaAttachments" (click)="openPreview(media)">{{ media.fileName }}</button>
            </div>
            <button mat-button type="button" (click)="setReplyTarget(comment)">Reply</button>
            <div class="reply-list" *ngIf="comment.replies?.length">
              <div class="reply-item" *ngFor="let reply of comment.replies">
                <div class="comment-meta">
                  <strong>{{ reply.createdByStaffName || reply.createdByName || 'Team member' }}</strong>
                  <span>{{ reply.createdAt | date:'medium' }}</span>
                </div>
                <p>{{ reply.comment }}</p>
                <div class="inline-media" *ngIf="reply.mediaAttachments?.length">
                  <button type="button" class="preview-link" *ngFor="let media of reply.mediaAttachments" (click)="openPreview(media)">{{ media.fileName }}</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ng-template #noComments>
          <div class="empty-comments">No updates yet.</div>
        </ng-template>
      </div>
    </section>

    <ng-template #emptyDetail>
      <section class="detail-studio panel detail-empty">
        <div>
          <p class="eyebrow">Task Detail</p>
          <h3>Select a task</h3>
          <p class="detail-sub">Choose a task from the rail to edit it, or create a new standalone or project-linked task from the modal.</p>
        </div>
        <button mat-flat-button color="primary" type="button" (click)="openCreateTaskModal()">
          <mat-icon>add</mat-icon>
          Create Task
        </button>
      </section>
    </ng-template>
  </div>
</div>
